import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ShieldAlert,
  Mail, Lock, Phone as PhoneIcon, User, Hash, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authApi } from "@/lib/api";

const DISCLAIMER_SECTIONS = [
  {
    h: "Risk of Loss",
    b: "Trading and investing are inherently risky. Market prices can move rapidly and unpredictably. Past performance, historical results, simulations, or examples do not guarantee future results.",
  },
  {
    h: "Only Risk Money You Can Afford to Lose",
    b: "Trade or invest only with money you can afford to lose without affecting your ability to pay for housing, food, healthcare, utilities, transportation, taxes, debt obligations, or other essential living expenses.",
  },
  {
    h: "Do Not Borrow Money to Trade",
    b: "Do not borrow money, take out a personal loan, use credit cards, or otherwise incur debt for the purpose of trading through TradingoX.",
  },
  {
    h: "Protect Your Savings and Emergency Funds",
    b: "Do not use retirement funds, emergency savings, education funds, life savings, or money reserved for essential personal or family needs unless you independently determine that doing so is appropriate for your financial circumstances and risk tolerance.",
  },
  {
    h: "Trading Is Not Gambling Money",
    b: "Do not treat TradingoX as a gambling platform or use money recklessly in an attempt to recover previous losses or generate quick profits.",
  },
  {
    h: "Do Not Trade If You Cannot Afford the Risk",
    b: "If you do not have discretionary capital available for trading, do not trade. You are never required or pressured by TradingoX to participate.",
  },
  {
    h: "TradingoX Is Not Appropriate for Everyone",
    b: "TradingoX is not designed for every person or every financial situation. You are responsible for determining whether trading and the use of TradingoX are appropriate for you.",
  },
  {
    h: "No Guaranteed Results",
    b: "TradingoX does not guarantee profits, investment returns, trading performance, or any particular financial outcome. Losses may occur even when the platform, system, or trading strategy operates as intended.",
  },
  {
    h: "You Make the Decision",
    b: "You remain responsible for your decision to participate, the amount of capital you make available for trading, your risk settings, and your continued use of the platform.",
  },
  {
    h: "Your Trading Capital Remains With Your Broker",
    b: "TradingoX does not take custody of your trading capital. Your trading funds remain in your brokerage account and are subject to the broker's terms, policies, safeguards, and account controls.",
  },
  {
    h: "Deposits and Withdrawals Are Handled by Your Broker",
    b: "TradingoX does not process withdrawals of your brokerage funds. Deposits, withdrawals, settlement requirements, processing times, and related account matters are handled by your broker. Please consult your broker's terms and policies for details.",
  },
  {
    h: "Trading Fee",
    b: "You agree to pay TradingoX a 1.25% trading fee on each qualifying profitable trade, subject to the TradingoX Terms of Service and applicable law. If a qualifying trade does not generate a profit, no success-based trading fee will be charged for that trade.",
  },
  {
    h: "No Guarantee of Satisfaction",
    b: "TradingoX cannot guarantee that the platform, trading strategy, execution, or results will meet your expectations. Any limitation of liability is governed by the TradingoX Terms of Service and applicable law.",
  },
];

const RISK_OPTIONS = [
  { value: "100", label: "$100" },
  { value: "200", label: "$200" },
  { value: "300", label: "$300" },
  { value: "400", label: "$400" },
  { value: "500+", label: "$500 or more" },
];

function detailToString(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

function Step1Disclaimer({ onAgree }) {
  const [agreed, setAgreed] = useState(false);
  return (
    <div
      data-testid="signup-step-disclaimer"
      className="rounded-2xl border border-rose-500/25 bg-[#0F111A]/80 backdrop-blur-sm shadow-[0_30px_120px_-30px_rgba(244,63,94,0.35)]"
    >
      <div className="px-6 pt-6 pb-5 border-b border-white/5">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-full bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/30 shrink-0">
            <ShieldAlert className="w-5 h-5" />
          </span>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-rose-300">
              risk disclosure · user acknowledgment
            </div>
            <h1 className="font-display text-xl sm:text-2xl font-extrabold text-slate-100 leading-tight">
              TradingoX Risk Disclosure
            </h1>
          </div>
        </div>

        {/* PROMINENT STOP NOTICE — must be the first thing an applicant reads */}
        <div
          data-testid="signup-stop-notice"
          className="mt-5 rounded-xl border-2 border-rose-500/60 bg-rose-500/15 shadow-[0_0_40px_-10px_rgba(244,63,94,0.55)] px-4 py-4 sm:px-5 sm:py-5"
        >
          <div className="flex items-center gap-2 justify-center text-rose-200 font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em]">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-bold">IMPORTANT NOTICE</span>
            <AlertTriangle className="w-4 h-4" />
          </div>
          <p
            data-testid="signup-stop-notice-text"
            className="mt-3 font-display font-black uppercase tracking-wide text-center text-rose-100 leading-[1.25] text-lg sm:text-xl md:text-2xl"
          >
            IF YOU CANNOT AFFORD TO LOSE THE MONEY YOU PLAN TO USE, OR IF YOU ARE STRUGGLING TO PAY FOR YOUR BASIC LIVING EXPENSES, LEAVE THIS SITE IMMEDIATELY. DO NOT CONTINUE WITH REGISTRATION OR DEPOSIT ANY MONEY.
          </p>
        </div>

        <div className="mt-4 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2.5 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-300 shrink-0" />
          <p className="text-[12px] sm:text-[13px] text-amber-100 font-mono uppercase tracking-[0.08em]">
            Please read the full disclosure carefully before using TradingoX
          </p>
        </div>
      </div>

      <div className="px-6 py-5 text-slate-200">
        <p className="text-[13px] sm:text-sm leading-relaxed">
          Trading and investing involve substantial financial risk. There is no guarantee that any trading
          strategy, automated system, technology, or investment approach will generate a profit. You may lose
          some or all of the capital you use for trading.
        </p>
        <p className="mt-3 text-[13px] sm:text-sm leading-relaxed text-slate-300">
          By using TradingoX, you acknowledge and agree to the following:
        </p>

        <ol
          data-testid="signup-disclaimer-list"
          className="mt-5 space-y-4"
        >
          {DISCLAIMER_SECTIONS.map((sec, i) => (
            <li
              key={sec.h}
              className="relative pl-9"
              data-testid={`signup-disclaimer-section-${i + 1}`}
            >
              <span
                aria-hidden="true"
                className="absolute left-0 top-0 grid place-items-center w-6 h-6 rounded-full bg-rose-500/15 text-rose-300 font-mono text-[11px] font-bold ring-1 ring-rose-500/25"
              >
                {i + 1}
              </span>
              <div className="font-display text-[13px] sm:text-sm font-bold text-slate-100 leading-snug">
                {sec.h}
              </div>
              <p className="mt-1 text-[12.5px] sm:text-[13px] text-slate-300 leading-relaxed">
                {sec.b}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-4 text-center">
          <div className="font-display text-sm sm:text-base font-extrabold tracking-[0.12em] text-emerald-200">
            YOUR MONEY. YOUR DECISION. YOUR RISK.
          </div>
          <div className="mt-1 text-[12px] sm:text-[13px] text-emerald-100/90 italic">
            Never trade with money you cannot afford to lose.
          </div>
        </div>

        <p className="mt-5 text-[12.5px] sm:text-[13px] text-slate-300 leading-relaxed">
          By proceeding, you acknowledge that you have read and understood this Risk Disclosure, understand
          that trading may result in substantial financial losses, and voluntarily accept the risks associated
          with your decision to trade.
        </p>
      </div>

      <div className="px-6 pb-6 pt-2 border-t border-white/5">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            data-testid="signup-agree-check"
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 w-5 h-5 rounded accent-emerald-500 cursor-pointer"
          />
          <span className="text-sm text-slate-200">
            I have read, understood, and <b className="text-emerald-300">agree</b> to the TradingoX
            Risk Disclosure &amp; User Acknowledgment above.
          </span>
        </label>

        <Button
          data-testid="signup-continue-btn"
          disabled={!agreed}
          onClick={() => onAgree()}
          className="mt-5 w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          Continue to sign-up form <ArrowRight className="w-4 h-4" />
        </Button>

        <p className="mt-3 text-center text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
          Already a member?{" "}
          <Link to="/" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2" data-testid="signup-signin-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, children }) {
  return (
    <div className="relative">
      {Icon && <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />}
      {children}
    </div>
  );
}

function Step2Form({ onBack, prefillRef }) {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referral, setReferral] = useState((prefillRef || "").toUpperCase());
  const [planToRisk, setPlanToRisk] = useState("");
  const [busy, setBusy] = useState(false);

  const refClean = useMemo(() => referral.trim().toUpperCase(), [referral]);

  const canSubmit =
    fullName.trim().length >= 2 &&
    phone.replace(/\D/g, "").length >= 7 &&
    /\S+@\S+\.\S+/.test(email) &&
    password.length >= 8 &&
    refClean.length >= 3;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || busy) return;
    try {
      setBusy(true);
      const r = await authApi.register({
        email: email.trim(),
        password,
        name: fullName.trim(),
        full_name: fullName.trim(),
        phone: phone.trim(),
        referred_by: refClean,
        plan_to_risk: planToRisk || null,
        age_confirmed: true,
        terms_accepted: true,
      });
      toast.success(r?.verification_sent ? "Account created — check your email to verify" : "Account created");
      navigate("/dashboard");
    } catch (err) {
      const msg = detailToString(err?.response?.data?.detail) || "Signup failed. Please check your info and try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="signup-step-form"
      className="rounded-2xl border border-emerald-500/25 bg-[#0F111A]/80 backdrop-blur-sm shadow-[0_30px_120px_-30px_rgba(16,185,129,0.35)]"
    >
      <div className="px-6 pt-6 pb-4 flex items-center gap-3 border-b border-white/5">
        <span className="grid place-items-center w-11 h-11 rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30">
          <CheckCircle2 className="w-5 h-5" />
        </span>
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300">step 2 · member registration</div>
          <h1 className="font-display text-xl sm:text-2xl font-extrabold text-slate-100">Create your account</h1>
        </div>
        <button
          type="button"
          onClick={onBack}
          data-testid="signup-back-btn"
          className="hidden sm:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-slate-400 hover:text-slate-200"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Re-read terms
        </button>
      </div>

      <div className="px-6 py-5 space-y-3">
        <Field icon={User}>
          <Input
            data-testid="signup-full-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Full name *"
            autoComplete="name"
            className="bg-[#0D0E14] border-white/10 h-12 pl-9"
          />
        </Field>
        <Field icon={PhoneIcon}>
          <Input
            data-testid="signup-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Telephone *"
            autoComplete="tel"
            className="bg-[#0D0E14] border-white/10 h-12 pl-9"
          />
        </Field>
        <Field icon={Mail}>
          <Input
            data-testid="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address *"
            autoComplete="email"
            className="bg-[#0D0E14] border-white/10 h-12 pl-9"
          />
        </Field>
        <Field icon={Lock}>
          <Input
            data-testid="signup-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (8+ characters) *"
            autoComplete="new-password"
            className="bg-[#0D0E14] border-white/10 h-12 pl-9"
          />
        </Field>

        <div>
          <Field icon={Hash}>
            <Input
              data-testid="signup-referral"
              value={referral}
              onChange={(e) => setReferral(e.target.value.toUpperCase())}
              placeholder="Referral ID *"
              className="bg-[#0D0E14] border-emerald-500/30 h-12 pl-9 uppercase tracking-[0.08em] font-mono"
            />
          </Field>
          <p className="mt-1.5 text-[11px] text-slate-400 pl-1">
            Required. Enter the referral ID of the member who invited you.{" "}
            <span className="text-emerald-300">
              No code yet? Text <a href="sms:+19013619173" className="underline underline-offset-2 hover:text-emerald-200 font-mono">901-361-9173</a> to request one.
            </span>
          </p>
        </div>

        <div className="pt-2">
          <label className="text-[12px] font-mono uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
            <Wallet className="w-3.5 h-3.5 text-emerald-300" />
            Trading capital (optional)
          </label>
          <p className="mt-1 text-[12px] text-slate-400 leading-snug">
            The recommended minimum to start trading is <b className="text-slate-200">$100</b> or more. How much do you plan to risk to trade?
          </p>
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="signup-plan-group">
            {RISK_OPTIONS.map((opt) => {
              const active = planToRisk === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`signup-plan-${opt.value}`}
                  onClick={() => setPlanToRisk(active ? "" : opt.value)}
                  aria-pressed={active}
                  className={`h-10 rounded-full border text-sm font-mono tracking-[0.05em] transition ${
                    active
                      ? "bg-emerald-500 text-black border-emerald-400 shadow-[0_0_0_3px_rgba(16,185,129,0.15)]"
                      : "bg-[#0D0E14] text-slate-200 border-white/10 hover:border-emerald-400/40 hover:text-emerald-200"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-6 pb-6 pt-2 border-t border-white/5">
        <Button
          type="submit"
          data-testid="signup-submit"
          disabled={!canSubmit || busy}
          className="w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-2"
        >
          {busy ? "Creating account…" : (<>Create my member account <ArrowRight className="w-4 h-4" /></>)}
        </Button>
        <p className="mt-3 text-center text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
          By creating an account you re-affirm all terms on the previous page.
        </p>
      </div>
    </form>
  );
}

export default function SignupPage() {
  const [step, setStep] = useState(1);
  const prefillRef = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const q = (p.get("ref") || "").trim();
      if (q) return q;
      return window.localStorage.getItem("tradingox_ref_code") || "";
    } catch { return ""; }
  }, []);

  useEffect(() => {
    // persist any ?ref= capture into localStorage for later re-use
    try {
      const p = new URLSearchParams(window.location.search);
      const q = (p.get("ref") || "").trim();
      if (q) window.localStorage.setItem("tradingox_ref_code", q.toUpperCase());
    } catch (err) { console.debug("[signup] ref capture failed", err); }
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100 relative overflow-x-hidden aq-grain">
      <div className="absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-emerald-500/10 via-transparent to-transparent pointer-events-none" />
      <header className="relative z-10 max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="signup-logo-link">
          <img
            src="/brand/tradingox-logo.png"
            alt="TradingoX"
            className="w-10 h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
          />
          <div className="font-display text-lg font-bold tracking-tight">TradingoX</div>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-100"
          data-testid="signup-home-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back home
        </Link>
      </header>

      <main className="relative z-10 max-w-xl mx-auto px-6 pb-14">
        <div className="mb-6 flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.3em]" data-testid="signup-stepper">
          <span className={step === 1 ? "text-emerald-300" : "text-slate-500"}>01 · Disclaimer</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className={step === 2 ? "text-emerald-300" : "text-slate-500"}>02 · Sign up</span>
        </div>

        {step === 1 && <Step1Disclaimer onAgree={() => setStep(2)} />}
        {step === 2 && <Step2Form onBack={() => setStep(1)} prefillRef={prefillRef} />}
      </main>
    </div>
  );
}
